import React from 'react';
import { useForm } from 'react-hook-form';
import { useEnquiryStore } from '../../store/useEnquiryStore';
import { QuoteDetails } from '../../types';

export const QuoteDetailsForm: React.FC = () => {
  const { currentEnquiry, updateQuoteDetails } = useEnquiryStore();
  const { register, handleSubmit } = useForm<QuoteDetails>({
    defaultValues: currentEnquiry.quoteDetails
  });

  const onSubmit = (data: QuoteDetails) => {
    updateQuoteDetails(data);
  };

  const handleInputChange = (field: keyof QuoteDetails, value: string) => {
    updateQuoteDetails({ [field]: value });
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Quote Details</h2>
        <p className="text-sm text-gray-600">Information about the quote and follow-up process.</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="dateSent" className="block text-sm font-medium text-gray-700 mb-1">
              Date Quote Sent
            </label>
            <input
              type="date"
              id="dateSent"
              {...register('dateSent')}
              onChange={(e) => handleInputChange('dateSent', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label htmlFor="method" className="block text-sm font-medium text-gray-700 mb-1">
              Quote Method
            </label>
            <select
              id="method"
              {...register('method')}
              onChange={(e) => handleInputChange('method', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="Email">Email</option>
              <option value="Post">Post</option>
              <option value="Fax">Fax</option>
              <option value="Hand">Hand Delivery</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="followUpDate" className="block text-sm font-medium text-gray-700 mb-1">
              Follow-up Date
            </label>
            <input
              type="date"
              id="followUpDate"
              {...register('followUpDate')}
              onChange={(e) => handleInputChange('followUpDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-1 text-sm text-gray-600">When to follow up with the customer</p>
          </div>

          <div>
            <label htmlFor="staffMember" className="block text-sm font-medium text-gray-700 mb-1">
              Staff Member
            </label>
            <input
              type="text"
              id="staffMember"
              {...register('staffMember')}
              onChange={(e) => handleInputChange('staffMember', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Staff member handling the quote"
            />
          </div>
        </div>

        <div>
          <label htmlFor="comments" className="block text-sm font-medium text-gray-700 mb-1">
            Quote Comments
          </label>
          <textarea
            id="comments"
            {...register('comments')}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Additional comments about the quote, special pricing, terms, etc."
          />
        </div>

        <div className="bg-yellow-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Quote Process Notes</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <ul className="list-disc list-inside space-y-1">
                  <li>Quotes are typically valid for 30 days</li>
                  <li>Follow up within 3-5 business days if no response</li>
                  <li>Include installation timeframes in the quote</li>
                  <li>Mention any special offers or discounts</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};